import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { favoritesAPI } from '../lib/api';
import { useAuth } from './AuthContext';

const FavoritesContext = createContext(null);

export const useFavorites = () => {
  const context = useContext(FavoritesContext);
  if (!context) return { favoriteIds: [], isFavorite: () => false, toggleFavorite: () => {}, count: 0, loading: false };
  return context;
};

export const FavoritesProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchIds = useCallback(async () => {
    if (!isAuthenticated) { setFavoriteIds([]); return; }
    setLoading(true);
    try {
      const res = await favoritesAPI.getIds();
      setFavoriteIds(res.data.listing_ids || []);
    } catch { setFavoriteIds([]); }
    finally { setLoading(false); }
  }, [isAuthenticated]);

  useEffect(() => { fetchIds(); }, [fetchIds]);

  const isFavorite = useCallback((listingId) => favoriteIds.includes(listingId), [favoriteIds]);

  const toggleFavorite = useCallback(async (listingId) => {
    if (!isAuthenticated) return false;
    const wasFav = favoriteIds.includes(listingId);
    // Optimistic update
    setFavoriteIds(prev => wasFav ? prev.filter(id => id !== listingId) : [...prev, listingId]);
    try {
      if (wasFav) {
        await favoritesAPI.remove(listingId);
      } else {
        await favoritesAPI.add(listingId);
      }
      return !wasFav;
    } catch {
      // Revert on error
      setFavoriteIds(prev => wasFav ? [...prev, listingId] : prev.filter(id => id !== listingId));
      return wasFav;
    }
  }, [isAuthenticated, favoriteIds]);

  return (
    <FavoritesContext.Provider value={{ favoriteIds, isFavorite, toggleFavorite, count: favoriteIds.length, loading }}>
      {children}
    </FavoritesContext.Provider>
  );
};
